import { useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { useCatalogServiceRates, useSaveServiceCatalog } from '@/components/Account/ServiceCatalog/hooks';
import { findServiceRate } from '@/components/Account/ServiceCatalog/utils';
import { buildServiceOption } from '../../../queries/serviceCatalog';
import {
    customerHasServiceDefinitions,
    getServiceCallbackLines,
    getServiceCallbackServiceTime,
    getServiceLines,
    groupServiceLinesByCode,
    isLastServiceCallbackLine,
    isServiceCallbackOrder,
} from '../../../utilities/serviceCallback';
import {
    applyCatalogDescriptions,
    buildServiceLineFromCatalog,
    canAddServiceLine,
    getServiceCatalogServices,
    getInvalidServiceLines,
} from '../../../utilities/serviceCatalog';
import { serviceCodeKey } from '../../../utilities/serviceLines';
import { quickAddCatalogDefaults, quickAddDimensionFields } from './utils';

export const useAddServiceRow = (order, onAdd, serviceCatalog) => {
    const options = useMemo(() => getServiceCatalogServices(serviceCatalog), [serviceCatalog]);
    const [selected, setSelected] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [quantity, setQuantity] = useState(1);

    const canAdd = useMemo(() => canAddServiceLine(selected, quantity), [selected, quantity]);

    const reset = () => {
        setSelected(null);
        setInputValue('');
        setQuantity(1);
    };

    const handleAdd = () => {
        if (!canAdd) return;
        onAdd(buildServiceLineFromCatalog(selected, quantity));
        reset();
    };

    return {
        serviceCatalog,
        options,
        selected,
        setSelected,
        inputValue,
        setInputValue,
        quantity,
        setQuantity,
        canAdd,
        handleAdd,
    };
};

// Derived data + local UI state for ServicesPanel: the grouped/validated service
// lines, callback meta (appt type + service time), per-code invalid flags, the
// add/edit/delete capability gates, and the pending-delete dialog state.
export const useServicesPanel = (
    order,
    { onDeleteGroup, onEditQuantity, onEditServiceTime, onAdd },
    serviceCatalog
) => {
    const isCallback = isServiceCallbackOrder(order);
    const lines = useMemo(
        () => (isCallback ? getServiceCallbackLines(order) : getServiceLines(order)),
        [order, isCallback]
    );
    // One row per code, quantities summed across the parser's duplicate rows, with
    // each row's description backfilled from the catalog when the line only echoes
    // its code.
    const groupedLines = useMemo(
        () => applyCatalogDescriptions(groupServiceLinesByCode(lines), serviceCatalog),
        [lines, order, serviceCatalog]
    );
    const apptType = order?.custom_fields?.service_callback?.appointment_type || 'Service Callback';
    const serviceTime = useMemo(() => (isCallback ? getServiceCallbackServiceTime(order) : null), [order, isCallback]);

    // invalid (red): a code that isn't a member of the order's catalog. Blocks
    // save. Pricing failures are not a per-line web concern — they stay
    // server-side and surface via pricing_errors. Keyed canonically because a
    // group's `code` is only one of its lines' raw casings.
    const invalidCodes = useMemo(
        () => new Set(getInvalidServiceLines(order, serviceCatalog).map((line) => serviceCodeKey(line.code))),
        [order, serviceCatalog]
    );

    // Add / delete are offered whenever the customer has service definitions — the reducers
    // initialize a missing `order.services` on first write, so an order with no service lines
    // yet still gets working Add/Delete. The typeof-function checks keep the panel safe to
    // render where callbacks are omitted.
    const hasServiceDefinitions = customerHasServiceDefinitions(order, serviceCatalog);
    const canDelete = typeof onDeleteGroup === 'function' && hasServiceDefinitions;
    const canEditQuantity = typeof onEditQuantity === 'function' && hasServiceDefinitions;
    const canEditServiceTime = typeof onEditServiceTime === 'function' && hasServiceDefinitions;
    const canAdd = typeof onAdd === 'function' && hasServiceDefinitions;

    // The grouped row awaiting delete confirmation (null = closed). Held as the whole group
    // so the confirm copy can name the quantity + code being removed.
    const [pendingDelete, setPendingDelete] = useState(null);
    // Deleting the only remaining code-group would orphan a service-callback order (empty
    // `services` fails validate()), so per-row Delete is disabled then. Mixed
    // delivery+services orders stay valid with zero services.
    const deleteDisabled = isLastServiceCallbackLine(order, groupedLines);

    return {
        isCallback,
        groupedLines,
        apptType,
        serviceTime,
        invalidCodes,
        canDelete,
        canEditQuantity,
        canEditServiceTime,
        canAdd,
        deleteDisabled,
        pendingDelete,
        setPendingDelete,
    };
};

// Quick add: put an invalid line's code into the shipper's catalog from the Services tab. The
// insert is immediate — it doesn't wait for the order's Save. On success the order's lines for the
// code are filled from the new entry and the shipper's catalogs refresh, which clears the row's
// invalid flag; the dialog stays in its saving state until that refresh lands. With catalog service
// rates on, the dialog's rate goes on the shipper's GE tariffs in the same write (or, if they didn't
// load, only the catalog is written; see useSaveServiceCatalog). `enabled` is false when quick add
// isn't offered, so the rate lookups don't run for nothing.
export const useQuickAddService = (order, shipperId, { onApplyCatalogService, onRefreshCatalogs, enabled = true }) => {
    // The dialog's { defaults, dimensionFields } while it's open. Built once on open: the form
    // reseeds whenever `defaults` changes identity, which would drop what the operator typed.
    const [pending, setPending] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState({});
    const serviceRates = useCatalogServiceRates(enabled ? shipperId : null);
    const { save: saveService } = useSaveServiceCatalog(serviceRates);

    // The code's rate row, if it has one: a code removed from the catalog keeps its tariff rows, so
    // adding it back shows that rate. Read from the current rows, not at open, since they can land
    // after the dialog opens (it stays locked until they do).
    const serviceRate = useMemo(
        () =>
            pending
                ? findServiceRate(
                      { code: pending.defaults.service_code, dimensions: pending.defaults.dimensions },
                      serviceRates.rows
                  )
                : null,
        [pending, serviceRates.rows]
    );

    const open = (group) =>
        setPending({
            defaults: quickAddCatalogDefaults(group, order),
            dimensionFields: quickAddDimensionFields(order),
        });
    const close = () => setPending(null);

    const save = async (payload, ratePayload) => {
        setSaving(true);
        try {
            const { data } = await saveService({ client_id: shipperId, ...payload }, ratePayload);
            const entry = buildServiceOption(data?.insert_service_catalog_one);
            if (entry) onApplyCatalogService(entry);
            await onRefreshCatalogs(shipperId);
            close();
        } catch (error) {
            console.error(error);
            captureException(error);
            setNotification({
                severity: 'error',
                message: `Could not add ${payload.service_code} to the catalog. It may already be there.`,
            });
            // Likeliest cause: the code was added since this catalog loaded. A refresh picks the
            // entry up, which clears the row.
            onRefreshCatalogs(shipperId);
        } finally {
            setSaving(false);
        }
    };

    return {
        pending,
        open,
        close,
        save,
        saving,
        serviceRates,
        serviceRate,
        notification,
        clearNotification: () => setNotification({}),
    };
};
